import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, Share } from 'lucide-react';
import { isInstalled } from '@/serviceWorkerRegistration';

/**
 * PWAInstallPrompt — "Add to Home Screen" banner
 * Works across all subdomain apps. Adapts color based on window.__ASTERPOS_PWA__
 */
export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Don't show if already installed or user dismissed
    if (isInstalled()) return;
    const dismissed = sessionStorage.getItem('pwa-install-dismissed');
    if (dismissed) return;

    // iOS detection (no beforeinstallprompt on Safari)
    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
    if (ios && isSafari && !isInstalled()) {
      setIsIOS(true);
      setTimeout(() => setShowBanner(true), 3000);
      return;
    }

    // Chrome/Edge/Android install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowBanner(true), 3000);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setShowBanner(false);
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleDismiss = () => {
    setShowBanner(false);
    sessionStorage.setItem('pwa-install-dismissed', '1');
  };

  if (!showBanner) return null;

  const pwa = window.__ASTERPOS_PWA__ || {};
  const accent = pwa.theme || '#f97316';
  const appTitle = pwa.title || 'Chain29';

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-4 right-4 z-[9999] mx-auto max-w-md animate-in slide-in-from-bottom-4 duration-500"
    >
      <div className="rounded-2xl border border-white/10 bg-gray-900/95 backdrop-blur-xl shadow-2xl p-4">
        <div className="flex items-start gap-3">
          {/* App icon */}
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: accent }}
          >
            <Download className="w-6 h-6 text-white" />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-sm leading-tight">
              Install {appTitle}
            </p>
            {isIOS ? (
              <p className="text-gray-400 text-xs mt-1 leading-snug">
                Tap <Share className="w-3 h-3 inline -mt-0.5" /> then <span className="font-medium text-gray-300">"Add to Home Screen"</span>
              </p>
            ) : (
              <p className="text-gray-400 text-xs mt-1">
                Add to your home screen for quick access
              </p>
            )}
          </div>

          <button
            onClick={handleDismiss}
            className="text-gray-500 hover:text-gray-300 shrink-0 p-1"
            data-testid="pwa-install-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {!isIOS && deferredPrompt && (
          <button
            onClick={handleInstall}
            className="w-full mt-3 py-2 rounded-xl text-white text-sm font-semibold transition-all hover:opacity-90 active:scale-[0.98]"
            style={{ background: accent }}
            data-testid="pwa-install-button"
          >
            Install App
          </button>
        )}
      </div>
    </div>
  );
}
